import { Caption } from '@remotion/captions';
import React, { useMemo, useRef } from 'react';
import { useCurrentFrame } from 'remotion';
import { CAPTIONS_FONT_SIZE } from '../constants';
import { FONT_FAMILY } from '../fonts/font';
import { FadingLines } from './FadingLines';
import { layoutText } from './helpers/get-number-of-lines-for-text';
import { msToFrame } from './helpers/ms-to-frame';
import { getSentenceToDisplay } from './helpers/sentence-to-display';
import { StaticLines } from './StaticLines';

const useWindowedFrameCaptions = ({
  captions,
  windowStart,
  windowEnd,
}: {
  captions: Caption[];
  windowStart: number;
  windowEnd: number;
}) => {
  return useMemo(() => {
    return captions.filter(({ startMs, endMs }) => {
      return msToFrame(startMs) >= windowStart && msToFrame(endMs) <= windowEnd;
    });
  }, [captions, windowEnd, windowStart]);
};

export const PaginatedCaptions: React.FC<{
  readonly captions: Caption[];
  readonly startFrame: number;
  readonly endFrame: number;
  readonly linesPerPage: number;
  readonly subtitlesTextColor: string;
  readonly onlyDisplayCurrentSentence: boolean;
  readonly textBoxWidth: number;
  readonly captionsType: string;
}> = ({
  startFrame,
  endFrame,
  captions,
  subtitlesTextColor: transcriptionColor,
  onlyDisplayCurrentSentence,
  textBoxWidth,
  captionsType,
}) => {
  const frame = useCurrentFrame();
  const windowRef = useRef<HTMLDivElement>(null);
  const windowedFrameSubs = useWindowedFrameCaptions({
    captions,
    windowStart: startFrame,
    windowEnd: endFrame,
  });

  const currentSentence = useMemo(() => {
    return getSentenceToDisplay({
      frame,
      onlyDisplayCurrentSentence,
      windowedFrameSubs,
    });
  }, [frame, onlyDisplayCurrentSentence, windowedFrameSubs]);

  const lines = useMemo(() => {
    return layoutText({
      captions: currentSentence,
      textBoxWidth,
      fontFamily: FONT_FAMILY,
      fontSize: CAPTIONS_FONT_SIZE,
    });
  }, [currentSentence, textBoxWidth]);

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        paddingBottom: '20px',
      }}
    >
      <div ref={windowRef}>
        {captionsType === 'static' ? (
          <StaticLines lines={lines} frame={frame} transcriptionColor={transcriptionColor} />
        ) : (
          <FadingLines lines={lines} frame={frame} transcriptionColor={transcriptionColor} />
        )}
      </div>
    </div>
  );
};
